import React, { useState } from 'react';
import './WaterSourceSelection.css';

const WaterSourceSelection = ({ wellData, onWaterSourceSelected }) => {
  // Initialize water source state
  const [sourceType, setSourceType] = useState('single');
  const [singleSource, setSingleSource] = useState('');
  const [multipleSources, setMultipleSources] = useState([
    { name: '', percent: 100 }
  ]);
  const [error, setError] = useState('');

  // Water source options
  const waterSourceOptions = [
    'Groundwater, < 1000TDS',
    'Groundwater, > 1000TDS',
    'Surface Water, < 1000TDS',
    'Surface Water, > 1000TDS',
    'Produced Water',
    'Other, < 1000TDS',
    'Other, > 1000TDS'
  ];

  // Handle single source change
  const handleSingleSourceChange = (value) => {
    setSingleSource(value);
  };

  // Handle multiple source change
  const handleMultipleSourceChange = (index, field, value) => {
    setMultipleSources(prev => {
      const newSources = [...prev];
      
      if (field === 'percent') {
        // Ensure percent is a number between 0 and 100
        value = Math.min(Math.max(0, parseFloat(value) || 0), 100);
      }
      
      newSources[index] = {
        ...newSources[index],
        [field]: value
      };
      
      return newSources;
    });
  };

  // Add a new water source
  const addWaterSource = () => {
    setMultipleSources(prev => {
      // Calculate remaining percentage
      const usedPercent = prev.reduce((sum, source) => sum + (parseFloat(source.percent) || 0), 0);
      const remainingPercent = Math.max(0, 100 - usedPercent);
      
      return [...prev, { name: '', percent: remainingPercent }];
    });
  };

  // Remove a water source
  const removeWaterSource = (index) => {
    setMultipleSources(prev => {
      const newSources = prev.filter((_, i) => i !== index);
      
      // If no water sources left, add one with 100%
      if (newSources.length === 0) {
        return [{ name: '', percent: 100 }];
      }
      
      // Redistribute the removed percentage to the first water source
      const totalPercent = newSources.reduce((sum, source) => sum + (parseFloat(source.percent) || 0), 0);
      const remainingPercent = 100 - totalPercent;
      
      if (remainingPercent > 0) {
        newSources[0] = {
          ...newSources[0],
          percent: (parseFloat(newSources[0].percent) || 0) + remainingPercent
        };
      }
      
      return newSources;
    });
  };

  // Validate water sources before continuing
  const validateWaterSources = () => {
    if (sourceType === 'single') {
      if (!singleSource) {
        setError('Please select a water source');
        return false;
      }
    } else {
      // Check if any water source name is empty
      const hasEmptyName = multipleSources.some(source => !source.name);
      if (hasEmptyName) {
        setError('Please select a water source type for all entries');
        return false;
      }
      
      // Check if percentages add up to 100
      const totalPercent = multipleSources.reduce((sum, source) => sum + (parseFloat(source.percent) || 0), 0);
      if (Math.abs(totalPercent - 100) > 0.1) { // Allow small rounding errors
        setError(`Water source percentages must add up to 100% (currently ${totalPercent.toFixed(1)}%)`);
        return false;
      }
    }
    
    setError('');
    return true;
  };

  // Handle continue button click
  const handleContinue = () => {
    if (!validateWaterSources()) {
      return;
    }
    
    // Format water sources based on type
    const waterSourceData = {
      type: sourceType
    };
    
    if (sourceType === 'single') {
      waterSourceData.source = singleSource;
    } else {
      waterSourceData.sources = multipleSources.map(source => ({
        name: source.name,
        percent: parseFloat(source.percent) || 0
      }));
    }
    
    onWaterSourceSelected(waterSourceData);
  };

  return (
    <div className="water-source-selection">
      <div className="water-source-header">
        <h2>Water Source Selection</h2>
        <p>Specify water sources for well: <strong>{wellData.name}</strong></p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="water-source-type-selector">
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="sourceType"
              value="single"
              checked={sourceType === 'single'}
              onChange={() => setSourceType('single')}
            />
            Single Water Source
          </label>
          <label>
            <input
              type="radio"
              name="sourceType"
              value="multiple"
              checked={sourceType === 'multiple'}
              onChange={() => setSourceType('multiple')}
            />
            Multiple Water Sources
          </label>
        </div>
      </div>
      
      {sourceType === 'single' ? (
        <div className="single-water-source">
          <div className="form-group">
            <label>Water Source</label>
            <select
              value={singleSource}
              onChange={(e) => handleSingleSourceChange(e.target.value)}
            >
              <option value="">Select a water source</option>
              {waterSourceOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div className="multiple-water-sources">
          {multipleSources.map((source, index) => (
            <div key={index} className="water-source-item">
              <div className="water-source-name">
                <label>Water Source</label>
                <select
                  value={source.name}
                  onChange={(e) => handleMultipleSourceChange(index, 'name', e.target.value)}
                >
                  <option value="">Select a water source</option>
                  {waterSourceOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div className="water-source-percent">
                <label>Percent (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={source.percent}
                  onChange={(e) => handleMultipleSourceChange(index, 'percent', e.target.value)}
                />
              </div>
              
              {multipleSources.length > 1 && (
                <button 
                  className="remove-source-button"
                  onClick={() => removeWaterSource(index)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          
          <button 
            className="add-source-button"
            onClick={addWaterSource}
            disabled={multipleSources.length >= 4}
          >
            Add Water Source
          </button>
        </div>
      )}
      
      <div className="water-source-actions">
        <button className="continue-button" onClick={handleContinue}>
          Continue to Chemical Disclosure
        </button>
      </div>
    </div>
  );
};

export default WaterSourceSelection;
